import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarBlank, Plus, Trash, Umbrella, FirstAid } from "@phosphor-icons/react";

const LEAVE_TYPES = { odihna: { label: "Concediu odihnă", icon: Umbrella, cls: "text-emerald-400" }, medical: { label: "Concediu medical", icon: FirstAid, cls: "text-red-400" } };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("ro-RO", { day: "2-digit", month: "short", year: "numeric" }) : "—");

export default function Leaves() {
  const { hasRole, user } = useAuth();
  const isAdmin = hasRole("admin");
  const [leaves, setLeaves] = useState([]);
  const [users, setUsers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [form, setForm] = useState({ user_id: "", type: "odihna", start: "", end: "", note: "" });
  const [cal, setCal] = useState({ country: "RO", year: new Date().getFullYear() });
  const [holidays, setHolidays] = useState([]);

  const loadLeaves = () => api.get("/wf/leaves").then((r) => setLeaves(r.data)).catch(() => {});
  const loadHolidays = (c, y) => api.get(`/wf/holidays?country=${c}&year=${y}`).then((r) => setHolidays(r.data.holidays)).catch(() => {});
  useEffect(() => {
    loadLeaves();
    api.get("/wf/countries").then((r) => setCountries(r.data)).catch(() => {});
    if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    loadHolidays("RO", new Date().getFullYear());
    // eslint-disable-next-line
  }, []);

  const add = async () => {
    try {
      await api.post("/wf/leaves", { ...form, user_id: isAdmin ? form.user_id || user.id : user.id });
      toast.success("Concediu adăugat"); setForm({ user_id: "", type: "odihna", start: "", end: "", note: "" }); loadLeaves();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/wf/leaves/${id}`); loadLeaves(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="leaves-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Calendar</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tighter">Concedii & Sărbători</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Concediile (odihnă/medical) și sărbătorile legale ale țărilor din Europa se iau în calcul automat la termenele din workflow.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Concedii */}
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="mb-3 font-head text-lg font-bold">Concedii</h3>
          <div className="mb-4 grid gap-2 rounded-lg border border-border/70 p-3">
            {isAdmin && (
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="leave-user"><SelectValue placeholder="Angajat…" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.department || u.role}</SelectItem>)}</SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="leave-type"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LEAVE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Notă" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
              <Input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="leave-start" />
              <Input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="leave-end" />
            </div>
            <Button onClick={add} data-testid="leave-add-btn"><Plus size={16} className="mr-1.5" /> Adaugă concediu</Button>
          </div>
          <div className="space-y-1.5">
            {leaves.map((lv) => {
              const meta = LEAVE_TYPES[lv.type] || LEAVE_TYPES.odihna; const Icon = meta.icon;
              return (
                <div key={lv.id} className="flex items-center gap-3 rounded-lg border border-border/70 px-3 py-2 text-sm" data-testid={`leave-${lv.id}`}>
                  <Icon size={18} className={meta.cls} />
                  <div className="flex-1">
                    <div className="font-medium">{lv.user_name || "—"} <span className="text-xs text-muted-foreground">· {meta.label}</span></div>
                    <div className="text-xs text-muted-foreground">{fmt(lv.start)} → {fmt(lv.end)} {lv.note ? `· ${lv.note}` : ""}</div>
                  </div>
                  <button onClick={() => del(lv.id)} className="text-muted-foreground hover:text-red-400"><Trash size={15} /></button>
                </div>
              );
            })}
            {leaves.length === 0 && <p className="text-sm text-muted-foreground">Niciun concediu înregistrat.</p>}
          </div>
        </div>

        {/* Sărbători */}
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-3 flex items-center gap-2"><CalendarBlank size={20} className="text-primary" /><h3 className="font-head text-lg font-bold">Sărbători legale</h3></div>
          <div className="mb-3 flex gap-2">
            <Select value={cal.country} onValueChange={(v) => { setCal({ ...cal, country: v }); loadHolidays(v, cal.year); }}>
              <SelectTrigger className="flex-1" data-testid="holiday-country"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">{countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" className="w-24" value={cal.year} onChange={(e) => { const y = Number(e.target.value) || cal.year; setCal({ ...cal, year: y }); loadHolidays(cal.country, y); }} data-testid="holiday-year" />
          </div>
          <div className="max-h-96 space-y-1 overflow-y-auto pr-1">
            {holidays.map((h) => (
              <div key={h.date} className="flex items-center justify-between rounded-lg border border-border/60 px-3 py-1.5 text-sm">
                <span>{h.name}</span><span className="font-mono text-xs text-muted-foreground">{fmt(h.date)}</span>
              </div>
            ))}
            {holidays.length === 0 && <p className="text-sm text-muted-foreground">Nicio sărbătoare găsită.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
